import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Spinner from "../../components/Shared/Spinner";
import Button from "../../components/Shared/Button";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import CBQBuilder, {
  CBQBuilderHandle,
} from "../../components/Dashboard/AddQuiz/CBQBuilder/CBQBuilder";
import {
  DraftCBQExam,
  hydrateDraft,
} from "../../components/Dashboard/AddQuiz/CBQBuilder/types";
import { GetAdminCBQExam } from "../../api/Quiz/GetAdminCBQExam";
import { GetAdminCBQScenarios } from "../../api/Quiz/GetAdminCBQScenarios";
import { GetAdminCBQScenarioQuestions } from "../../api/Quiz/GetAdminCBQScenarioQuestions";
import { CBQQuestionDTO } from "../../api/Quiz/InitCBQQuestion";
import { PublishCBQExam } from "../../api/Quiz/PublishCBQExam";
import { UpdateCBQExam } from "../../api/Quiz/UpdateCBQExam";
import { GetAllQuizs } from "../../api/Quiz/GetAllQuizs";
import { getChapters } from "../../api/Chapters/GetAllChapters";
import { ChaptersDTO } from "../../api/Chapters/GetAllChapters";

// Loads an existing exam and its full scenario/question tree, then hands it to
// the same builder used for creation. The builder persists every edit as it is
// made, so this page has no save step — only publish.
const loadExam = async (examId: string) => {
  const examRes = await GetAdminCBQExam(examId);
  if (examRes.error || !examRes.data)
    throw new Error(examRes.error?.message || "Could not load CBQ exam");
  const exam = examRes.data;

  // Detail may inline scenarios; fall back to the dedicated endpoint.
  let scenarios = exam.scenarios ?? [];
  if (!scenarios.length) {
    const scenarioRes = await GetAdminCBQScenarios(examId);
    if (scenarioRes.error)
      throw new Error(scenarioRes.error.message || "Could not load scenarios");
    scenarios = scenarioRes.data ?? [];
  }

  const questionLists = await Promise.all(
    scenarios.map((s) => GetAdminCBQScenarioQuestions(s.id))
  );
  const questionsByScenario: Record<string, CBQQuestionDTO[]> = {};
  scenarios.forEach((s, i) => {
    questionsByScenario[s.id] = questionLists[i].data ?? [];
  });

  return { exam, scenarios, questionsByScenario };
};

const EditCbqExam = () => {
  const { courseId = "", examId = "" } = useParams();
  const navigate = useNavigate();
  const builderRef = useRef<CBQBuilderHandle>(null);
  const [draft, setDraft] = useState<DraftCBQExam | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryFn: () => loadExam(examId),
    queryKey: ["admin-cbq-exam", examId],
    enabled: !!examId,
    // The builder owns the draft once hydrated; refetching would clobber
    // in-progress edits with a stale server snapshot.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // ...but that snapshot must not outlive the page. The builder writes every
    // edit straight to the API without touching this cache, so a retained entry
    // would re-hydrate the next visit from pre-edit data and make saved work
    // look lost. Dropping it on unmount forces a fresh read each time.
    gcTime: 0,
  });

  // MCQ quizzes for the PROD_WITH_MCQ gate selector.
  const { data: quizsDTO } = useQuery({
    queryFn: () => GetAllQuizs(courseId),
    queryKey: ["quiz", courseId],
    enabled: !!courseId,
  });

  // Course structure, for the lecture this exam is linked to.
  const { data: chaptersDTO } = useQuery({
    queryFn: () => getChapters(courseId),
    queryKey: ["chapters", courseId],
    enabled: !!courseId,
  });
  const chapters = (chaptersDTO?.data as ChaptersDTO[] | undefined) ?? [];
  const lectureOptions: option[] = chapters.flatMap((c) =>
    (c.lectures ?? []).map((l) => ({
      id: l.id,
      name: `${c.name} — ${l.title}`,
    }))
  );

  // Held locally so the select reflects the change immediately; the exam itself
  // is patched on selection rather than waiting for the next builder edit.
  const [lectureId, setLectureId] = useState<string>("");
  useEffect(() => {
    if (data?.exam.lectureId) setLectureId(data.exam.lectureId);
  }, [data]);

  const selectedLecture =
    lectureOptions.find((o) => o.id === lectureId) ?? null;

  // The PROD_WITH_MCQ gate should offer the linked lecture's own quizzes; fall
  // back to every quiz in the course while no lecture is linked.
  const lectureQuizzes =
    chapters
      .flatMap((c) => c.lectures ?? [])
      .find((l) => l.id === lectureId)?.quizizz ??
    quizsDTO?.data?.quiz ??
    [];

  const handleLectureChange = async (opt: option | null) => {
    const nextId = opt?.id ?? "";
    if (!nextId || nextId === lectureId) return;
    const previous = lectureId;
    setLectureId(nextId);
    const res = await UpdateCBQExam({
      id: examId,
      payload: { lectureId: nextId },
    });
    if (res.error) {
      setLectureId(previous);
      toast.error(res.error.message || "Could not link exam to lecture");
      return;
    }
    toast.success("Lecture updated");
  };

  // Hydrate once, when the fetch lands. Never re-run: `draft` is the live
  // editing state from that point on.
  useEffect(() => {
    if (!data || draft) return;
    setDraft(
      hydrateDraft(data.exam, data.scenarios, data.questionsByScenario)
    );
  }, [data, draft]);

  // The builder's onChange takes a non-null draft (or an updater over one),
  // while this page holds `null` until hydration. Only rendered post-hydration,
  // so a null prev here can't happen.
  const handleDraftChange = (
    updater: DraftCBQExam | ((prev: DraftCBQExam) => DraftCBQExam)
  ) =>
    setDraft((prev) =>
      typeof updater === "function"
        ? prev
          ? updater(prev)
          : prev
        : updater
    );

  const handlePublish = async () => {
    setIsPublishing(true);
    await builderRef.current?.flushPending();
    const res = await PublishCBQExam(examId);
    setIsPublishing(false);
    if (res.error) {
      const m = res.error.message as unknown;
      toast.error(
        Array.isArray(m)
          ? (m as string[]).join("\n")
          : (m as string) || "Could not publish CBQ exam"
      );
      return;
    }
    toast.success("CBQ exam published");
    navigate(`/dashboard/courses/${courseId}/cbq-exams`);
  };

  // PUBLISHED is the only locked state: the publish endpoint takes DRAFT/READY
  // only, and the server rejects scenario/question init and delete underneath a
  // published exam. Offering the button again would just surface a 400.
  const isPublished =
    (data?.exam.status || "").toLowerCase() === "published";

  const handleDone = async () => {
    // Every edit is already persisted; just flush debounced text patches.
    await builderRef.current?.flushPending();
    toast.success("Changes saved");
    navigate(`/dashboard/courses/${courseId}/cbq-exams`);
  };

  if (isLoading || (data && !draft))
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Spinner />
      </div>
    );

  // A failed load must never fall through to an empty builder — editing one
  // would wipe the exam it failed to read.
  if (error || !draft)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-red-700">
          {(error as Error)?.message || "Could not load CBQ exam"}
        </p>
        <button
          className="underline text-primary"
          onClick={() => navigate(`/dashboard/courses/${courseId}/cbq-exams`)}
        >
          Back to CBQ exams
        </button>
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center py-7 gap-4">
        <div>
          <h1 className="text-burntBlack font-bold text-[32px]">
            Edit CBQ Exam
          </h1>
          <p className="text-sm text-gray-500">
            Changes save as you type. Status: {data?.exam.status || "created"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleDone}
            className="!h-12 w-32 !text-sm font-bold rounded-md"
            variant="gray"
          >
            Done
          </Button>
          {!isPublished && (
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
              className="!h-12 w-32 !text-sm font-bold rounded-md"
              variant="primary"
            >
              {isPublishing ? "Publishing…" : "Publish"}
            </Button>
          )}
        </div>
      </div>

      <CBQBuilder
        ref={builderRef}
        value={draft}
        onChange={handleDraftChange}
        existingExamId={examId}
        lectureId={lectureId || undefined}
        mcqQuizzes={lectureQuizzes}
        beforeTitle={
          <div>
            <Selectbox
              title="Lecture"
              placeholder={
                lectureOptions.length
                  ? "Select a lecture"
                  : "No lectures on this course"
              }
              selectOptions={lectureOptions}
              selected={selectedLecture}
              setSelected={
                ((opt: option | null) =>
                  handleLectureChange(opt)) as React.Dispatch<
                  React.SetStateAction<option | null>
                >
              }
            />
            <p className="text-sm text-gray-500 mt-1">
              Where students reach this exam from. Saved on selection.
            </p>
          </div>
        }
      />
    </div>
  );
};

export default EditCbqExam;
